import { styled } from "goober";
import type { CSSProperties, ReactNode } from "react";
import { Box } from "../Box";
import { Text } from "../Text";
import { fieldOutline } from "../fieldOutline";

export const FormCard = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: var(--surface-secondary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
`;

export const PriceGrid = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PriceRow = styled("div")`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: center;
`;

export const ReadOnlyCell = styled("div")`
  height: 32px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--alpha4);
  outline: 0.5px solid var(--border-alpha);
  color: var(--text-primary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SpendList = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

export const SpendRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
`;

export const IconContainer = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  color: var(--text-primary);
`;

export const RowLabel = styled("div")`
  flex: 1;
  min-width: 0;
`;

export const fullWidthStyle = { minWidth: 0, width: "100%" } as const;

const SectionHeadingWrap = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 8px;
`;

export function SectionHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <SectionHeadingWrap>
      <Text variant="text-regular-md">{title}</Text>
      <Text variant="text-small" color="var(--text-secondary)">
        {description}
      </Text>
    </SectionHeadingWrap>
  );
}

export function ModalField({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">{label}</Text>
      </Box>
      {children}
      {description ? (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {description}
          </Text>
        </Box>
      ) : null}
    </div>
  );
}

const MoneyField = styled("div")`
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  height: 32px;
  background-color: var(--surface-primary);
  ${fieldOutline("&:focus-within")}
  border-radius: 8px;
  overflow: hidden;

  &[data-disabled="true"] {
    background-color: var(--alpha4);
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 8px 12px;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--text-primary);
    font-size: 12px;
    font-family: InterVariable;
    line-height: 16px;

    &::placeholder {
      opacity: 0.3;
    }
  }
`;

const MoneySuffix = styled("div")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 12px;
  background: var(--alpha4);
  border-left: 0.5px solid var(--border-alpha);

  &[data-disabled="true"] {
    background: transparent;
    border-left-color: transparent;
  }
`;

function formatThousands(value: string): string {
  const [intPart = "", decimalPart] = value.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart === undefined ? grouped : `${grouped}.${decimalPart}`;
}

export function UsdInput({
  value,
  placeholder,
  disabled,
  onChange,
  style,
}: {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  style?: CSSProperties;
}) {
  return (
    <MoneyField style={style} data-disabled={disabled}>
      <input
        value={formatThousands(value)}
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value.replace(/,/g, "");
          if (next === "" || /^\d*\.?\d*$/.test(next)) onChange(next);
        }}
      />
      <MoneySuffix data-disabled={disabled}>
        <Text variant="text-small-md" color="var(--text-secondary)">
          USD
        </Text>
      </MoneySuffix>
    </MoneyField>
  );
}
